import React, { useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { HiXMark, HiArrowsPointingOut, HiMinus, HiChevronUpDown, HiArrowsPointingIn } from 'react-icons/hi2';
import { useOptionalModalManager } from './ModalManager';
import { usePerformance } from '../../contexts/PerformanceContext';
import { useKeyboardShortcut } from '../../hooks/useKeyboardShortcut';
import { useKeyboardShortcutsContext } from '../../contexts/KeyboardShortcutsContext';

export interface BaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showCloseButton?: boolean;
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
  className?: string;
  resizable?: boolean;
  retractable?: boolean;
  stickyFooter?: boolean;
  footerContent?: React.ReactNode;
  // New props for minimize functionality
  onMinimize?: () => void;
  isMinimized?: boolean;
  modalId?: string;
  actionIcon?: React.ReactNode;
  toolbarActionId?: string;
  onRestore?: () => void;
  // Standardized modal layout props
  icon?: React.ReactNode;
  description?: string;
  useStandardLayout?: boolean;
}

export const BaseModal: React.FC<BaseModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  showCloseButton = true,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  className = '',
  resizable = false,
  stickyFooter = false,
  footerContent,
  onMinimize,
  isMinimized = false,
  modalId,
  actionIcon,
  toolbarActionId,
  onRestore,
  // Standardized layout props
  icon,
  description,
  useStandardLayout = false
}) => {
  const { performanceMode } = usePerformance();
  const isPerformanceMode = performanceMode === 'performance';
  
  const modalRef = useRef<HTMLDivElement>(null);
  const [modalSize, setModalSize] = useState({ width: 0, height: 0 });
  const [originalSize, setOriginalSize] = useState({ width: 0, height: 0 });
  const [isMaximized, setIsMaximized] = useState(false);
  const [isAnimatingToToolbar, setIsAnimatingToToolbar] = useState(false);
  const [isAnimatingFromToolbar, setIsAnimatingFromToolbar] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [justFinishedResizing, setJustFinishedResizing] = useState(false);
  const wasMinimizedRef = useRef(false);
  const resizeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const modalManager = useOptionalModalManager();
  const removeModalFromManager = modalManager?.removeModal;
  const { enabled: shortcutsEnabled } = useKeyboardShortcutsContext();

  // Initialize modal size based on size prop
  useEffect(() => {
    if (isOpen && !modalSize.width) {
      const sizeMap = {
        sm: { width: 448, height: 400 }, // max-w-md
        md: { width: 512, height: 500 }, // max-w-lg  
        lg: { width: 672, height: 600 }, // max-w-2xl
        xl: { width: 896, height: 700 }  // max-w-4xl
      };
      const initialSize = sizeMap[size];
      setModalSize(initialSize);
      setOriginalSize(initialSize);
    }
  }, [isOpen, size, modalSize.width]);

  // Handle modal cleanup when actually closed (not minimized)
  useEffect(() => {
    if (!isOpen && modalId && removeModalFromManager) {
      removeModalFromManager(modalId);
    }
  }, [isOpen, modalId, removeModalFromManager]);

  // Keyboard shortcuts for modal
  useKeyboardShortcut(
    'escape',
    () => {
      if (shortcutsEnabled && closeOnEscape && isOpen) {
        onClose();
      }
    },
    {
      enabled: shortcutsEnabled && isOpen,
      description: 'Fechar Modal',
      category: 'modals'
    }
  );

  useKeyboardShortcut(
    'ctrl+m',
    () => {
      if (shortcutsEnabled && onMinimize && isOpen && !isMinimized) {
        onMinimize();
      }
    },
    {
      enabled: shortcutsEnabled && isOpen && !!onMinimize,
      description: 'Minimizar Modal',
      category: 'modals'
    }
  );

  // Handle restore animation from minimized state
  useEffect(() => {
    if (isMinimized) {
      wasMinimizedRef.current = true;
    } else if (isOpen && wasMinimizedRef.current) {
      // Modal is being restored from minimized state
      setIsAnimatingFromToolbar(true);
      wasMinimizedRef.current = false;
      
      setTimeout(() => {
        setIsAnimatingFromToolbar(false);
      }, 400); // Match animation duration
    }
  }, [isOpen, isMinimized]);

  // Handle escape key
  useEffect(() => {
    if (!closeOnEscape || !isOpen || isMinimized) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closeOnEscape, onClose, isMinimized]);

  // Focus management
  useEffect(() => {
    if (isOpen && modalRef.current && !isMinimized) {
      modalRef.current.focus();
    }
  }, [isOpen, isMinimized]);

  // Prevent body scroll when modal is open and not minimized
  useEffect(() => {
    if (isOpen && !isMinimized) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
      // Cleanup any lingering resize state
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.body.style.pointerEvents = '';
      // Clear resize timeout
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
        resizeTimeoutRef.current = null;
      }
    };
  }, [isOpen, isMinimized]);

  // Handle resize functionality
  const handleResize = useCallback((event: MouseEvent) => {
    if (!modalRef.current || !resizable) return;

    event.preventDefault();
    event.stopPropagation();

    const startX = event.clientX;
    const startY = event.clientY;
    const startWidth = modalSize.width;
    const startHeight = modalSize.height;

    // Create a reference to the current modal for direct DOM manipulation
    const modalElement = modalRef.current;

    // Enable resize mode
    setIsResizing(true);
    
    // Add visual feedback during resize
    document.body.style.cursor = 'se-resize';
    document.body.style.userSelect = 'none';
    
    // Prevent any clicks during resize
    document.body.style.pointerEvents = 'none';
    if (modalElement) {
      modalElement.style.pointerEvents = 'auto'; // Keep modal interactive
    }
    let animationFrameId: number;

    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation(); // Prevent event bubbling during resize
      
      // Cancel previous animation frame
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      
      // Use requestAnimationFrame for smooth updates
      animationFrameId = requestAnimationFrame(() => {
        const deltaX = e.clientX - startX;
        const deltaY = e.clientY - startY;
        
        const newWidth = Math.max(300, Math.min(window.innerWidth * 0.95, startWidth + deltaX));
        const newHeight = Math.max(200, Math.min(window.innerHeight * 0.95, startHeight + deltaY));
        
        // Direct DOM manipulation for immediate response during resize
        if (modalElement) {
          modalElement.style.width = `${newWidth}px`;
          modalElement.style.height = `${newHeight}px`;
        }
        
        // Update state for final value
        setModalSize({ width: newWidth, height: newHeight });
      });
    };

    const handleMouseUp = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation(); // Prevent event bubbling to overlay
      
      // Cancel any pending animation frame
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      
      // Disable resize mode
      setIsResizing(false);
      
      // Set flag to prevent overlay click for a short period
      setJustFinishedResizing(true);
      
      // Clear any existing timeout
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      
      // Reset the flag after a short delay
      resizeTimeoutRef.current = setTimeout(() => {
        setJustFinishedResizing(false);
      }, 200); // 200ms protection period
      
      // Reset cursor and selection
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.body.style.pointerEvents = ''; // Restore pointer events
      if (modalElement) {
        modalElement.style.pointerEvents = ''; // Reset modal pointer events
      }
      
      document.removeEventListener('mousemove', handleMouseMove, { capture: true });
      document.removeEventListener('mouseup', handleMouseUp, { capture: true });
    };

    // Use capture phase to ensure we get the events
    document.addEventListener('mousemove', handleMouseMove, { capture: true });
    document.addEventListener('mouseup', handleMouseUp, { capture: true });
  }, [modalSize, resizable]);

  // Handle minimize to toolbar
  const handleMinimize = useCallback(async () => {
    if (!modalId || !onMinimize) return;

    // Get toolbar position for animation target
    const toolbar = document.querySelector('[data-toolbar="floating"]');
    const modalElement = modalRef.current;
    
    if (toolbar && modalElement) {
      setIsAnimatingToToolbar(true);
      
      // Add to minimized modals in toolbar
      if (modalManager) {
        const fallbackRestore = () => {
          // Fallback: just remove from minimized list
          if (modalManager) {
            modalManager.removeModal(modalId);
          }
        };
        
        const modalData = {
          id: modalId,
          title: title || 'Modal',
          icon: actionIcon,
          ...(toolbarActionId && { toolbarActionId }),
          onRestore: onRestore || fallbackRestore
        };
        modalManager.minimizeModal(modalData);
      }
      
      // Don't close modal, just minimize it after shrink animation
      setTimeout(() => {
        if (onMinimize) onMinimize();
        setIsAnimatingToToolbar(false);
      }, 400); // Match shrink animation duration
    } else {
      // Fallback if toolbar not found - just minimize
      if (onMinimize) onMinimize();
    }
  }, [modalId, onMinimize, title, actionIcon, modalManager, toolbarActionId, onRestore]);

  // Handle maximize/restore
  const handleToggleMaximize = useCallback(() => {
    if (isMaximized) {
      // Restore to original size
      setModalSize(originalSize);
      setIsMaximized(false);
    } else {
      // Maximize to 90% of screen
      const maxSize = {
        width: window.innerWidth * 0.9,
        height: window.innerHeight * 0.9
      };
      setModalSize(maxSize);
      setIsMaximized(true);
    }
  }, [isMaximized, originalSize]);

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg', 
    lg: 'max-w-2xl',
    xl: 'max-w-4xl'
  };

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 }
  };

  const getToolbarPosition = () => {
    const toolbar = document.querySelector('[data-toolbar="floating"]');
    if (toolbar) {
      const rect = toolbar.getBoundingClientRect();
      // Calculate position relative to center of screen for framer motion
      return {
        x: rect.left + rect.width / 2 - window.innerWidth / 2,
        y: rect.top + rect.height / 2 - window.innerHeight / 2
      };
    }
    // Fallback to bottom-right area
    return { x: window.innerWidth * 0.35, y: window.innerHeight * 0.35 };
  };

  // Create simple shrink and fade animation to bottom right corner
  const createShrinkFadeVariant = (isMinimizing: boolean) => {
    const toolbarPos = getToolbarPosition();
    
    if (isMinimizing) {
      return {
        opacity: 0,
        scale: 0.1, // Shrink to small size
        x: toolbarPos.x,
        y: toolbarPos.y,
      };
    } else {
      return {
        opacity: 1,
        scale: 1,
        x: 0,
        y: 0,
      };
    }
  };

  const modalVariants = {
    hidden: { 
      opacity: 0, 
      scale: 0.95,
      y: 20
    },
    visible: { 
      opacity: 1, 
      scale: 1,
      y: 0,
      x: 0,
    },
    exit: {
      opacity: 0,
      scale: 0.95,
      y: 20
    },
    shrinkMinimize: createShrinkFadeVariant(true),
    shrinkRestore: createShrinkFadeVariant(false)
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (closeOnOverlayClick && e.target === e.currentTarget && !isAnimatingToToolbar && !isResizing && !justFinishedResizing) {
      onClose();
    }
  };

  // Don't render if not open
  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence mode="wait" initial={false}>
      {!isMinimized && (
        <>
          {/* Backdrop */}
          <motion.div
          initial="hidden"
          animate="visible"
          exit="hidden"
          variants={overlayVariants}
          transition={{ duration: isPerformanceMode ? 0 : 0.2 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          onClick={handleOverlayClick}
        >
          {/* Backdrop with blur - Enhanced depth */}
          <div 
            className="absolute inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-xl"
            style={{ 
              backdropFilter: isPerformanceMode ? 'none' : 'blur(20px)',
              WebkitBackdropFilter: isPerformanceMode ? 'none' : 'blur(20px)',
              boxShadow: 'inset 0 0 100px rgba(0, 0, 0, 0.3)'
            }}
          />

            {/* Modal Content */}
            <motion.div
          ref={modalRef}
          tabIndex={-1}
          {...(modalId && !isResizing && { layoutId: modalId })} // Disable layout animations during resize
          variants={modalVariants}
          initial="hidden"
          animate={
            isResizing 
              ? false // Disable framer motion animations during resize
              : isAnimatingToToolbar 
                ? "shrinkMinimize" 
                : isAnimatingFromToolbar 
                  ? "shrinkRestore" 
                  : "visible"
          }
          exit={isAnimatingToToolbar ? "shrinkMinimize" : "exit"}
          transition={
            isResizing || isPerformanceMode
              ? { duration: 0 } // No transition during resize or performance mode
              : {
                  type: "tween",
                  duration: isAnimatingToToolbar || isAnimatingFromToolbar ? 0.4 : 0.3,
                  ease: isAnimatingToToolbar 
                    ? [0.25, 0.1, 0.25, 1] // Smooth ease out for minimize
                    : isAnimatingFromToolbar
                      ? [0.175, 0.885, 0.32, 1.1] // Gentle overshoot for restore
                      : [0.25, 0.46, 0.45, 0.94] // Normal
                }
          }
          className={`
            relative pointer-events-auto
            ${!resizable ? sizeClasses[size] : ''}
            ${className}
          `}
          style={{
            width: resizable ? `${modalSize.width}px` : undefined,
            height: resizable ? `${modalSize.height}px` : undefined,
            maxHeight: resizable ? 'none' : '90vh',
            maxWidth: resizable ? 'none' : undefined,
            willChange: isResizing ? 'width, height' : 'transform, opacity, filter',
            minWidth: resizable ? '300px' : undefined,
            minHeight: resizable ? '200px' : undefined,
            borderRadius: '20px',
            transformOrigin: 'center center', // Center origin for shrink animation
            transform: 'translateZ(0)', // Force hardware acceleration
            backfaceVisibility: 'hidden',
            // Disable any CSS transitions during resize
            transition: isResizing ? 'none' : undefined,
            boxShadow: `
              0 25px 50px -12px rgba(0, 0, 0, 0.25),
              0 16px 32px -8px rgba(0, 0, 0, 0.15),
              0 8px 16px -4px rgba(0, 0, 0, 0.1),
              0 0 0 1px rgba(255, 255, 255, 0.15),
              inset 0 1px 0 rgba(255, 255, 255, 0.2)
            `
          }}
        >
          {/* Glass Base Layer */}
          <div
            className="absolute inset-0 z-10 bg-white/10 dark:bg-neutral-900/95"
            style={{
              borderRadius: 'inherit',
              backdropFilter: isResizing ? "blur(6px)" : "blur(16px)",
              WebkitBackdropFilter: isResizing ? "blur(6px)" : "blur(16px)",
              willChange: isResizing ? 'none' : 'transform',
              transform: 'translateZ(0)',
              transition: isResizing ? 'none' : undefined
            }}
          />
          
          {/* Main Glass Layer - Light Mode Gradient */}
          <div 
            className="absolute inset-0 z-20 border border-white/20 dark:border-neutral-700/50 dark:hidden"
            style={{
              borderRadius: 'inherit',
              background: `linear-gradient(135deg, 
                rgba(255,255,255,0.90) 0%, 
                rgba(255,255,255,0.85) 25%,
                rgba(255,255,255,0.80) 50%, 
                rgba(255,255,255,0.85) 75%,
                rgba(255,255,255,0.90) 100%)`,
              backdropFilter: isResizing ? "none" : "blur(4px)",
              WebkitBackdropFilter: isResizing ? "none" : "blur(4px)",
              transition: isResizing ? 'none' : undefined
            }}
          />
          
          {/* Main Glass Layer - Dark Mode Gradient */}
          <div 
            className="absolute inset-0 z-20 border border-white/20 dark:border-neutral-700/50 hidden dark:block"
            style={{
              borderRadius: 'inherit',
              background: `linear-gradient(135deg, 
                rgba(38,38,38,0.95) 0%, 
                rgba(23,23,23,0.98) 25%,
                rgba(15,15,15,0.99) 50%, 
                rgba(23,23,23,0.98) 75%,
                rgba(38,38,38,0.95) 100%)`,
              backdropFilter: isResizing ? "none" : "blur(4px)",
              WebkitBackdropFilter: isResizing ? "none" : "blur(4px)",
              transition: isResizing ? 'none' : undefined
            }}
          />

          {/* Inner Glass Reflection - Light Mode */}
          <div
            className="absolute inset-0 z-30 dark:hidden"
            style={{
              borderRadius: 'inherit',
              background: isResizing ? 'none' : `linear-gradient(145deg, 
                rgba(255,255,255,0.6) 0%, 
                transparent 25%,
                transparent 75%,
                rgba(255,255,255,0.4) 100%)`,
              boxShadow: isResizing ? 'none' : `inset 1px 1px 2px rgba(255,255,255,0.6), 
                inset -1px -1px 2px rgba(255,255,255,0.3)`,
              transition: isResizing ? 'none' : undefined
            }}
          />
          
          {/* Inner Glass Reflection - Dark Mode */}
          <div
            className="absolute inset-0 z-30 hidden dark:block"
            style={{
              borderRadius: 'inherit',
              background: isResizing ? 'none' : `linear-gradient(145deg, 
                rgba(64,64,64,0.4) 0%, 
                transparent 25%,
                transparent 75%,
                rgba(32,32,32,0.3) 100%)`,
              boxShadow: isResizing ? 'none' : `inset 1px 1px 2px rgba(64,64,64,0.4), 
                inset -1px -1px 2px rgba(32,32,32,0.2)`,
              transition: isResizing ? 'none' : undefined
            }}
          />
          
          {/* Surface Highlights - Light Mode */}
          <div
            className="absolute inset-0 z-40 pointer-events-none dark:hidden"
            style={{
              borderRadius: 'inherit',
              background: isResizing ? 'none' : `radial-gradient(circle at 30% 20%, 
                rgba(255,255,255,0.7) 0%, 
                transparent 50%)`,
              opacity: isResizing ? 0 : 0.5,
              transition: isResizing ? 'none' : undefined
            }}
          />
          
          {/* Surface Highlights - Dark Mode */}
          <div
            className="absolute inset-0 z-40 pointer-events-none hidden dark:block"
            style={{
              borderRadius: 'inherit',
              background: isResizing ? 'none' : `radial-gradient(circle at 30% 20%, 
                rgba(96,96,96,0.4) 0%, 
                transparent 50%)`,
              opacity: isResizing ? 0 : 0.5,
              transition: isResizing ? 'none' : undefined
            }}
          />

          {/* Resize Handle - bottom right corner */}
          {resizable && (
            <div
              className={`absolute bottom-0 right-0 z-60 w-6 h-6 cursor-se-resize bg-transparent hover:bg-white/10 flex items-center justify-center group ${isResizing ? '' : 'transition-colors'}`}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation(); // Prevent overlay click
                handleResize(e.nativeEvent);
              }}
              title="Redimensionar modal"
            >
              <div className="w-3 h-3 opacity-50 group-hover:opacity-80 transition-opacity">
                <HiChevronUpDown className="w-3 h-3 text-gray-500 dark:text-gray-400 rotate-45" />
              </div>
            </div>
          )}

          {/* Content Container */}
          <div 
            className="relative z-50 flex flex-col h-full bg-white/10 dark:bg-neutral-950/30" 
            style={{ 
              borderRadius: 'inherit',
              // Ensure content doesn't interfere with resize handle
              paddingRight: resizable ? '6px' : '0',
              paddingBottom: resizable ? '6px' : '0'
            }}
          >
            {/* Header */}
            {useStandardLayout ? (
              /* Standardized Header with Centered Icon */
              <div className="px-4 pt-4 pb-3 border-b border-white/10 dark:border-neutral-700/30 flex-shrink-0">
                {/* Close Button Row */}
                {showCloseButton && (
                  <div className="flex items-center justify-end space-x-2 mb-3">
                    {onMinimize && modalId && (
                      <button
                        onClick={handleMinimize}
                        className="p-2 rounded-lg text-gray-400 hover:text-yellow-600 dark:hover:text-yellow-400 hover:bg-yellow-50/10 transition-all duration-200"
                        title="Minimizar para toolbar"
                      >
                        <HiMinus className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={handleToggleMaximize}
                      className="p-2 rounded-lg text-gray-400 hover:text-green-600 dark:hover:text-green-400 hover:bg-green-50/10 transition-all duration-200"
                      title={isMaximized ? "Restaurar tamanho" : "Maximizar modal"}
                    >
                      {isMaximized ? <HiArrowsPointingIn className="w-4 h-4" /> : <HiArrowsPointingOut className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={onClose}
                      className="p-2 rounded-lg text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50/10 transition-all duration-200"
                    >
                      <HiXMark className="w-5 h-5" />
                    </button>
                  </div>
                )}
                
                {/* Centered Icon */}
                {(icon || actionIcon) && (
                  <div className="flex justify-center mb-3">
                    <div className={`flex items-center justify-center w-12 h-12 rounded-full border ${(() => {
                      if (icon) {
                        return 'bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border-blue-200/50 dark:border-blue-700/50';
                      }
                      
                      if (actionIcon) {
                        const iconType = (actionIcon as React.ReactElement).type;
                        const iconName = typeof iconType === 'function' ? iconType.name : '';
                        
                        // Map common action icons to appropriate background colors
                        if (iconName.includes('UserPlus') || iconName.includes('User')) {
                          return 'bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border-blue-200/50 dark:border-blue-700/50';
                        }
                        if (iconName.includes('ArrowUpTray') || iconName.includes('Upload')) {
                          return 'bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 border-orange-200/50 dark:border-orange-700/50';
                        }
                        if (iconName.includes('ChatBubble') || iconName.includes('Comment')) {
                          return 'bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 border-green-200/50 dark:border-green-700/50';
                        }
                        if (iconName.includes('Check') || iconName.includes('Success')) {
                          return 'bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 border-green-200/50 dark:border-green-700/50';
                        }
                        if (iconName.includes('Pencil') || iconName.includes('Edit')) {
                          return 'bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border-blue-200/50 dark:border-blue-700/50';
                        }
                        if (iconName.includes('Trash') || iconName.includes('Delete')) {
                          return 'bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 border-red-200/50 dark:border-red-700/50';
                        }
                        if (iconName.includes('Warning') || iconName.includes('Exclamation')) {
                          return 'bg-gradient-to-br from-yellow-50 to-yellow-100 dark:from-yellow-900/20 dark:to-yellow-800/20 border-yellow-200/50 dark:border-yellow-700/50';
                        }
                      }
                      
                      // Default background
                      return 'bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border-blue-200/50 dark:border-blue-700/50';
                    })()}`}>
                      {icon || (actionIcon && (() => {
                        // Get icon color based on action context
                        const getIconColor = () => {
                          const iconType = (actionIcon as React.ReactElement).type;
                          const iconName = typeof iconType === 'function' ? iconType.name : '';
                          
                          // Map common action icons to appropriate colors
                          if (iconName.includes('UserPlus') || iconName.includes('User')) return 'text-blue-500';
                          if (iconName.includes('ArrowUpTray') || iconName.includes('Upload')) return 'text-orange-500';
                          if (iconName.includes('ChatBubble') || iconName.includes('Comment')) return 'text-green-500';
                          if (iconName.includes('Check') || iconName.includes('Success')) return 'text-green-500';
                          if (iconName.includes('Pencil') || iconName.includes('Edit')) return 'text-blue-500';
                          if (iconName.includes('Trash') || iconName.includes('Delete')) return 'text-red-500';
                          if (iconName.includes('Warning') || iconName.includes('Exclamation')) return 'text-yellow-500';
                          
                          // Default color
                          return 'text-blue-500';
                        };
                        
                        return React.cloneElement(actionIcon as React.ReactElement, {
                          className: `w-8 h-8 ${getIconColor()}`
                        } as any);
                      })())}
                    </div>
                  </div>
                )}
                
                {/* Centered Title */}
                {title && (
                  <div className="text-center mb-1">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {title}
                    </h2>
                  </div>
                )}
                
                {/* Centered Description */}
                {description && (
                  <div className="text-center">
                    <p className="text-xs text-gray-600 dark:text-gray-400 leading-normal max-w-sm mx-auto">
                      {description}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              /* Legacy Header Layout */
              (title || showCloseButton) && (
                <div className="flex items-center justify-between p-4 border-b border-white/10 dark:border-neutral-700/30 flex-shrink-0">
                  {title && (
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {title}
                    </h2>
                  )}
                  {showCloseButton && (
                    <div className="flex items-center space-x-2">
                      {onMinimize && modalId && (
                        <button
                          onClick={handleMinimize}
                          className="p-2 rounded-lg text-gray-400 hover:text-yellow-600 dark:hover:text-yellow-400 hover:bg-yellow-50/10 transition-all duration-200"
                          title="Minimizar para toolbar"
                        >
                          <HiMinus className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={handleToggleMaximize}
                        className="p-2 rounded-lg text-gray-400 hover:text-green-600 dark:hover:text-green-400 hover:bg-green-50/10 transition-all duration-200"
                        title={isMaximized ? "Restaurar tamanho" : "Maximizar modal"}
                      >
                        {isMaximized ? <HiArrowsPointingIn className="w-4 h-4" /> : <HiArrowsPointingOut className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={onClose}
                        className="p-2 rounded-lg text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50/10 transition-all duration-200"
                      >
                        <HiXMark className="w-5 h-5" />
                      </button>
                    </div>
                  )}
                </div>
              )
            )}

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto min-h-0">
              {children}
            </div>

            {/* Sticky Footer */}
            {stickyFooter && footerContent && (
              <div className="flex-shrink-0 border-t border-white/10 dark:border-neutral-700/30 bg-white/10 dark:bg-neutral-950/50">
                {footerContent}
              </div>
            )}
          </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};
